<template>
  <BubbleMenu :editor="editor" :tippyOptions="{ zIndex: 500 }">
    <div
      class="bg-surface-0 border border-surface-200 rounded-md flex gap-2 p-2 isolate text-base"
    >
      <FloatLabel variant="on">
        <Select
          class="min-w-32 isolate"
          v-model="selectedBlockType"
          :options="blockTypeOptions"
          optionLabel="label"
          optionValue="value"
          optionGroupLabel="label"
          optionGroupChildren="items"
          @change="onBlockTypeChange"
        >
          <template #option="{ option }">
            <div class="flex gap-3 items-center">
              <i :class="option.icon" />
              <span>{{ option.label }}</span>
            </div>
          </template>
          <template #optiongroup="{ option }">
            <div class="text-sm">
              <span>{{ option.label }}</span>
            </div>
          </template>
        </Select>
        <label>Type</label>
      </FloatLabel>

      <div class="flex gap-1">
        <ToggleButton
          size="small"
          :disabled="!editor.can().toggleBold()"
          @click="editor.chain().focus().toggleBold().run()"
          :modelValue="isBold"
        >
          <i class="fa-solid fa-bold" />
        </ToggleButton>

        <ToggleButton
          size="small"
          :disabled="!editor.can().toggleItalic()"
          @click="editor.chain().focus().toggleItalic().run()"
          :modelValue="isItalic"
        >
          <i class="fa-solid fa-italic" />
        </ToggleButton>

        <ToggleButton
          size="small"
          @click="editor.chain().focus().toggleUnderline().run()"
          :modelValue="isUnderline"
          :disabled="!editor.can().toggleUnderline()"
        >
          <i class="fa-solid fa-underline" />
        </ToggleButton>

        <ToggleButton
          size="small"
          @click="editor.chain().focus().toggleStrike().run()"
          :modelValue="isStrike"
          :disabled="!editor.can().toggleStrike()"
        >
          <i class="fa-solid fa-strikethrough" />
        </ToggleButton>
      </div>
    </div>
  </BubbleMenu>
</template>

<script setup lang="ts">
  import { BubbleMenu } from "@tiptap/vue-3";
  import FloatLabel from "primevue/floatlabel";
  import Select from "primevue/select";
  import ToggleButton from "primevue/togglebutton";
  import type { Editor } from "@tiptap/core";
  import { type Level } from "@tiptap/extension-heading";

  const props = defineProps<{
    editor: Editor;
  }>();

  const isBold = computed(() => props.editor.isActive("bold") ?? false);
  const isItalic = computed(() => props.editor.isActive("italic") ?? false);
  const isUnderline = computed(
    () => props.editor.isActive("underline") ?? false
  );
  const isStrike = computed(() => props.editor.isActive("strike") ?? false);

  const blockTypeOptions = [
    {
      label: "Hierarchy",
      items: [
        {
          label: "Paragraph",
          value: "paragraph",
          icon: "fa-solid fa-paragraph",
        },
        { label: "Heading 1", value: "heading-1", icon: "fa-solid fa-heading" },
        { label: "Heading 2", value: "heading-2", icon: "fa-solid fa-heading" },
        { label: "Heading 3", value: "heading-3", icon: "fa-solid fa-heading" },
        { label: "Heading 4", value: "heading-4", icon: "fa-solid fa-heading" },
      ],
    },
    {
      label: "Lists",
      items: [
        {
          label: "Bullet List",
          value: "bulletList",
          icon: "fa-solid fa-list-ul",
        },
        {
          label: "Ordered List",
          value: "orderedList",
          icon: "fa-solid fa-list-ol",
        },
      ],
    },
  ];

  const selectedBlockType = computed(() => {
    if (props.editor.isActive("heading", { level: 1 })) return "heading-1";
    if (props.editor.isActive("heading", { level: 2 })) return "heading-2";
    if (props.editor.isActive("heading", { level: 3 })) return "heading-3";
    if (props.editor.isActive("heading", { level: 4 })) return "heading-4";
    if (props.editor.isActive("bulletList")) return "bulletList";
    if (props.editor.isActive("orderedList")) return "orderedList";
    return "paragraph";
  });

  const onBlockTypeChange = (event: { value: string }) => {
    const type = event.value;

    if (type.startsWith("heading-")) {
      const level = parseInt(type.split("-")[1] ?? "1");
      props.editor
        .chain()
        .focus()
        .toggleHeading({ level: level as Level })
        .run();
    } else if (type === "bulletList") {
      props.editor.chain().focus().toggleBulletList().run();
    } else if (type === "orderedList") {
      props.editor.chain().focus().toggleOrderedList().run();
    } else {
      props.editor.chain().focus().setParagraph().run();
    }
  };
</script>
